// Shared bootstrap for block behaviour that must work both on the front end and
// inside the block editor.
//
// On the front end a block's DOM exists when its (deferred) script runs, so a
// one-off init is enough. In the editor, ACF renders block previews via AJAX
// after the page has loaded and re-renders them whenever a field changes, so
// the same init has to run again for each fresh preview. ACF announces those
// renders with the `render_block_preview/type=<name>` JS action; this helper
// wires an init function to that action and guards against double-initialising
// an element.
//
// When the editor canvas is iframed (WP 6.3+ with API v3 blocks) our scripts run
// inside the iframe while ACF's JS lives in the parent window, so `acf` is looked
// up on the parent as a fallback.

export type BlockInit = ( root: ParentNode ) => void;

interface AcfLike {
	addAction: ( name: string, callback: ( ...args: unknown[] ) => void ) => void;
}

const INIT_FLAG = 'zenithInit';

function getAcf(): AcfLike | undefined {
	const own = ( window as unknown as { acf?: AcfLike } ).acf;
	if ( own && typeof own.addAction === 'function' ) {
		return own;
	}
	try {
		if ( window.parent && window.parent !== window ) {
			const parentAcf = ( window.parent as unknown as { acf?: AcfLike } ).acf;
			if ( parentAcf && typeof parentAcf.addAction === 'function' ) {
				return parentAcf;
			}
		}
	} catch {
		// Cross-origin parent: not an editor context.
	}
	return undefined;
}

/**
 * Query `selector` within `root`, including `root` itself when it matches.
 */
export function findAll<T extends Element = HTMLElement>( root: ParentNode, selector: string ): T[] {
	const found = Array.from( root.querySelectorAll<T>( selector ) );
	if ( root instanceof Element && root.matches( selector ) ) {
		found.unshift( root as unknown as T );
	}
	return found;
}

/**
 * Mark an element as initialised for `key`; returns false if it already was.
 */
export function once( el: HTMLElement, key: string ): boolean {
	const flag = `${ INIT_FLAG }${ key }`;
	if ( el.dataset[ flag ] ) {
		return false;
	}
	el.dataset[ flag ] = '1';
	return true;
}

/**
 * Run `init` now (or on DOMContentLoaded) and again for every editor preview of
 * the given ACF block type.
 *
 * @param blockName ACF block name without the `acf/` prefix, e.g. 'faq'.
 * @param init      Initialiser scoped to a root node.
 */
export function onBlockRender( blockName: string, init: BlockInit ): void {
	const run = (): void => init( document );

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', run, { once: true } );
	} else {
		run();
	}

	const acf = getAcf();
	if ( ! acf ) {
		return;
	}

	acf.addAction( `render_block_preview/type=${ blockName }`, ( $el: unknown ) => {
		// ACF passes a jQuery collection; unwrap to the element.
		const el = ( $el as { 0?: Element } )?.[ 0 ] ?? ( $el as Element );
		if ( el instanceof Element ) {
			init( el );
		}
	} );
}
